import type { FC, ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import { cn } from './cn.js';

/** Тон плашки - как .alert.warn / .error / .info / .success макета. */
export type AlertTone = 'warn' | 'error' | 'info' | 'success';

const TONE: Record<AlertTone, string> = {
  warn: 'border-[#fde68a] bg-[#fffbeb] text-[#92400e]',
  error: 'border-[#fecaca] bg-[#fef2f2] text-[#991b1b]',
  info: 'border-[#bfdbfe] bg-[#eff6ff] text-[#1d4ed8]',
  success: 'border-brand-green-100 bg-brand-green-50 text-brand-green-700',
};

const ICON = {
  warn: AlertTriangle,
  error: XCircle,
  info: Info,
  success: CheckCircle2,
} as const;

export interface AlertProps {
  tone: AlertTone;
  children: ReactNode;
  /** Иконка вместо иконки тона - когда макет рисует свою (календарь у плана). */
  icon?: LucideIcon;
  className?: string;
}

/** Плашка-сообщение внутри экрана или окна. Ошибка - с role="alert". */
export const Alert: FC<AlertProps> = ({ tone, children, icon, className }) => {
  const Icon = icon ?? ICON[tone];

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2.5 rounded-lg border px-3.5 py-3 text-[13px]',
        TONE[tone],
        className,
      )}
    >
      <Icon aria-hidden className="mt-px size-4 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
};
